import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'

//Providers
import { GlobalController } from 'src/app/controllers/global.controller'

//Controllers
import { StorageController } from './../controllers/storage.controller'

import { serverAddresses } from './server.provider'
import { Router } from '@angular/router'

@Injectable({
  providedIn: 'root',
})
export class AuthProvider {
  public token: any = ''
  public user: any = null
  public isAuthenticated = false
  server = serverAddresses.api

  constructor(
    public http: HttpClient,
    private readonly globalCtrl: GlobalController,
    private readonly storageCtrl: StorageController,
    private readonly router: Router,
  ) { }

  async initAuth() {
    return Promise.all([this.getUser(), this.getToken()])
  }

  checkAuthentication() {
    return new Promise((resolve, reject) => {
      this.storageCtrl.get('token').then(
        token => {
          this.token = token
          resolve(token)
        },
        error => reject(error)
      )
    })
  }

  setToken(token) {
    this.token = 'Bearer ' + token
    this.storageCtrl.set('token', this.token)
  }

  async setUser(user) {
    this.user = user
    await this.storageCtrl.set('user', this.user)
  }

  async setLoginInfo(accessToken: string, user: UserLogin, onComplete?: () => void, redirectTo?: string) {
    this.setToken(accessToken)
    await this.setUser(user)
    this.globalCtrl.setLanguage(this.getUserLanguage())

    if (onComplete) {
      try {
        onComplete();
      } catch (err) {
        console.log('err', err)
      }

    }

    const navigateTo = redirectTo
      ? this.router.navigate([redirectTo])
      : this.router.navigateByUrl(this.getNavigateUrlByRole(user.role));

    navigateTo.then(() => {
      this.globalCtrl.toggleMenu();
      this.globalCtrl.showConfToast('Bonjour ' + user.email);
    });
  }

  private getUser() {
    return new Promise((resolve, reject) => {
      if (this.user) {
        resolve(this.user)
      } else {
        this.storageCtrl
          .get('user')
          .then(user => {
            if (user) {
              this.user = user
              resolve(this.user)
            } else {
              resolve(null)
            }
          })
          .catch(err => {
            console.error(err)
            resolve(null)
          })
      }
    })
  }

  private getToken() {
    return new Promise((resolve, reject) => {
      if (this.token) {
        resolve(this.token)
      } else {
        this.storageCtrl
          .get('token')
          .then(token => {
            if (token) {
              this.token = token
              resolve(this.token)
            } else {
              resolve(null)
            }
          })
          .catch(err => {
            console.error(err)
            resolve(null)
          })
      }
    })
  }

  login(credentials) {
    return new Promise<LoginResponse>((resolve, reject) => {
      const headers = new HttpHeaders().set('app-version', this.globalCtrl.appVersion.number)
      this.http
        .post<LoginResponse>(this.server + '/login', credentials, {
          headers: headers,
          withCredentials: true,
        })
        .subscribe(
          res => {
            resolve(res)
          },
          err => {
            console.error(err)
            reject(err)
          }
        )
    })
  }

  logout() {
    this.token = null
    this.user = null
    this.setToken(null)
    this.setUser(null)
    this.storageCtrl.clear()
    const headers = new HttpHeaders().set('app-version', this.globalCtrl.appVersion.number)
    // logout in the API deletes jwt cookies.
    this.http.get(this.server + '/logout', { headers: headers, withCredentials: true }).subscribe()
  }

  getUserRole() {
    return this.user && this.user.role ? this.user.role : 'employee'
  }

  getUserId() {
    return this.user && this.user._id ? this.user._id : null
  }

  getUserFirstName() {
    return this.user && this.user.firstName ? this.user.firstName : null
  }

  getUserFullName() {
    return this.user && this.user.firstName && this.user.lastName
      ? this.user.firstName + ' ' + this.user.lastName
      : null
  }

  getUserLanguage() {
    return this.user && this.user.language ? this.user.language : 'fr'
  }

  changePassword(newPassword, token) {
    return new Promise((resolve, reject) => {
      let headers = new HttpHeaders().set('app-version', this.globalCtrl.appVersion.number)
      headers = headers.append('Authorization', 'Bearer ' + token)
      this.http
        .put(this.server + '/forgot-password', newPassword, {
          headers: headers,
          withCredentials: true,
        })
        .subscribe(
          (res: any) => {
            resolve(res)
          },
          err => {
            console.error(err)
            reject(err)
          }
        )
    })
  }

  async sendPasswordResetEmailOrSMS(credential:EmailOrPhone) {
    return new Promise((resolve, reject) => {
      const headers = new HttpHeaders().set('app-version', this.globalCtrl.appVersion.number)
      this.http.post(this.server + '/forgot-password', credential, { headers: headers }).subscribe(
        (res: any) => {
          resolve(res)
        },
        err => {
          console.error(err)
          reject(err)
        }
      )
    })
  }

  loginWithMagicLink(magicToken) {
    return new Promise<LoginResponse>((resolve, reject) => {
      const headers = new HttpHeaders().set('app-version', this.globalCtrl.appVersion.number)
      this.http.post<LoginResponse>(this.server + '/login-magic-link', { "token": magicToken }, { headers: headers }).subscribe(
        res => {
          resolve(res)
        },
        err => {
          console.error(err)
          reject(err)
        }
      )
    })
  }

  getNavigateUrlByRole(role) {
    switch (role) {
      case 'admin':
        return '/overview?1'
      case 'employee':
        return '/time-entries'
      case 'supervisor':
        return '/dashboard-supervisor'
    }
  }
}

export interface LoginResponse {
  "accessToken": string,
  "refreshToken": string,
  "user": UserLogin
}

interface UserLogin {
  _id: string;
  role: string;
  firstName: string;
  lastName: string;
  language: string;
  email: string;
}

type EmailOrPhone = 
  | { email: string }
  | { phone: string };
